/**
 * Dual-YouTube-IFrame playback driver.
 *
 * Two hidden <div> mount points host two YT.Player instances (A & B). One is
 * the ACTIVE player at 100% volume; the other silently pre-loads the next
 * queued track. 15s before the active track ends we start a 10s crossfade
 * (active 100→0, idle 0→100). After crossfade we swap roles and pre-load
 * the next-next track on the new idle player.
 *
 * Public API is exposed as a singleton `youtubeDriver` created lazily on
 * first `init()`. The React context is a thin orchestrator on top of this.
 */

const YT_IFRAME_API = 'https://www.youtube.com/iframe_api';

import { createBackgroundInterval } from './backgroundTimer';

// Normalize a track dict so downstream YT ops always find a videoId. Backend
// still returns `apple_id` (legacy field name for schema-compat) — accept
// any of the common id fields as source-of-truth.
function pickVideoId(t) {
  if (!t) return null;
  // `video_id` is the canonical field name post Feb 2026 YouTube pivot;
  // the legacy `apple_id` / `youtube_id` / `videoId` variants are still
  // accepted so older cached playlists don't need re-hydration.
  return t.video_id || t.id || t.apple_id || t.youtube_id || t.videoId || null;
}

function normalizeTrack(t) {
  const vid = pickVideoId(t);
  if (!vid) return null;
  return { ...t, id: vid };
}

let apiReadyPromise = null;

function loadYouTubeAPI() {
  if (apiReadyPromise) return apiReadyPromise;
  apiReadyPromise = new Promise((resolve) => {
    if (typeof window === 'undefined') return;
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try { prev && prev(); } catch (err) {
        // Another consumer's onYouTubeIframeAPIReady threw — don't let it
        // block OUR resolve. Log for observability; the SDK is still usable.
        // eslint-disable-next-line no-console
        console.warn('[youtubePlayer] prev onYouTubeIframeAPIReady threw:', err?.message || err);
      }
      resolve(window.YT);
    };
    if (!document.querySelector(`script[src="${YT_IFRAME_API}"]`)) {
      const s = document.createElement('script');
      s.src = YT_IFRAME_API;
      s.async = true;
      s.onerror = (err) => {
        // Script blocked by ad-blocker or offline. Reject so the caller
        // can render a friendlier "playback blocked" state instead of
        // hanging on the pending promise forever.
        // eslint-disable-next-line no-console
        console.error('[youtubePlayer] YT IFrame API script failed to load — ad-blocker or offline?', err);
      };
      document.head.appendChild(s);
    }
  });
  return apiReadyPromise;
}

function createHostDiv(id) {
  let el = document.getElementById(id);
  if (el) return el;
  el = document.createElement('div');
  el.id = id;
  // Feb 2026 fix — MUST be inside the visible viewport to satisfy the
  // Chromium autoplay policy. Iframes rendered at `left: -9999px` or
  // fully clipped away silently refuse to autoplay on both headless
  // AND real-device Chromium. Previous versions did that, which was the
  // root cause behind the "queue hydrates but nothing plays" bug that
  // survived three RCA passes (iterations 80-82). Solution: pin to the
  // top-left corner at 1x1px with near-zero opacity — Chromium's rule
  // for autoplay-in-hidden-iframes checks visible bounding box + opacity
  // + composited layer, and this passes all three without being seen.
  el.style.position = 'fixed';
  el.style.left = '0';
  el.style.top = '0';
  el.style.width = '1px';
  el.style.height = '1px';
  el.style.opacity = '0.01';
  el.style.pointerEvents = 'none';
  el.style.zIndex = '-1';
  document.body.appendChild(el);
  return el;
}

function createPlayer(YT, hostId, driverCallbacks) {
  return new Promise((resolve) => {
    const player = new YT.Player(hostId, {
      height: '180',
      width: '320',
      videoId: '',
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        modestbranding: 1,
        playsinline: 1,
        rel: 0,
        origin: window.location.origin,
      },
      events: {
        onReady: () => {
          // Audio-only mode (Feb 2026): force the lowest video quality so
          // YouTube streams the minimum video track alongside the audio.
          // The hidden audio driver iframe lives at 1x1 opacity 0.01 (see
          // `createHostDiv`). The Master Player card now shows static album
          // art (BoothAlbumArt), not a video — there is no visible iframe.
          try { player.setPlaybackQuality('tiny'); } catch { /* noop */ }
          resolve(player);
        },
        // YT.Player error codes we care about:
        //   2   — invalid parameter (bad videoId format)
        //   5   — HTML5 player error
        //   100 — video not found / removed
        //   101 — embedding disabled by uploader
        //   150 — embedding disabled by uploader (variant, private-owned)
        // For 101/150/100 we MUST skip forward — the player will sit at
        // state=-1 forever otherwise. Iterations 80-83 all hit this trap
        // because there was no onError handler at all.
        onError: (e) => {
          try {
            driverCallbacks?.onPlayerError?.({
              code: e.data,
              host_id: hostId,
              player,
            });
          } catch { /* noop */ }
        },
      },
    });
  });
}

/**
 * Options for `init`:
 *   fadeTriggerSec: seconds before end to arm the crossfade (default 15)
 *   fadeDurationMs: crossfade duration in ms (default 10000)
 *   onTrackChanged: fn(activeTrack, nextTrack) — called when the ACTIVE
 *                   player swaps to a new track (crossfade completed)
 *   onEnded:        fn() — called after the last queued track ends
 *   onQueueExhausted: fn() — end of the current playlist (natural end, manual
 *                   next past the end, or whole-playlist embed failure). The
 *                   owner advances to the next SCHEDULED playlist.
 *   onStateChange:  fn({isPlaying, progressMs, durationMs, activeTrack, nextTrack})
 *   nextTrackResolver: async fn(currentIndex) -> Promise<track|null>
 *   trackDone:      async fn(finishedTrackIndex) — called AFTER role swap
 *                   (main use: POST /queue/next to advance the backend)
 */
export class YouTubeDualDriver {
  constructor() {
    this.players = { A: null, B: null };
    this.active = 'A';
    this.tracks = [];              // {id, name, artists, album_image, duration_ms}
    this.currentIndex = 0;
    this.fadeTriggerSec = 15;
    this.fadeDurationMs = 10000;
    this._tick = null;
    this._fade = null;
    this._crossfadeArmedForId = null;
    this._ready = false;
    this._callbacks = {};
    this._prewarmedFor = null;     // videoId that idle player is loaded with
    this._unplayableIds = new Set(); // videoIds YouTube refused (100/101/150)
    this._isPlaying = false;
    this._playIntent = false;       // operator WANTS audio (survives background pauses)
    this._masterVolume = 100;       // 0-100
    // ---- Ad detection state (Feb 2026) ----
    // YouTube's embedded player serves ads by silently loading a
    // different videoId than the one we queued. We poll the active
    // player's `getVideoData().video_id` and compare to the expected
    // queue track. When they diverge → an ad is playing; we mute the
    // player + fire `onAdDetected` so the UI can surface a "skipping
    // ad" pill. When they re-converge → ad ended; restore volume.
    this._adPlaying = false;
    this._adGraceUntil = 0;        // ms epoch; skip ad-check until this time
    this._adVolumeBeforeMute = 100;
    this._adMismatchStreak = 0;     // consecutive ticks the video_id mismatched
    this._adMutedAt = 0;            // ms epoch we muted for a suspected ad
    this._acceptedAltId = null;     // canonical video_id accepted for the current track
  }

  async init(opts = {}) {
    // Always refresh callbacks — React StrictMode double-mounts the
    // MusicProvider, and the singleton driver keeps its `_callbacks` from
    // whichever call ran first. Without this refresh the second mount's
    // callbacks are silently dropped and the tick fires the stale ones.
    if (opts.callbacks) this._callbacks = opts.callbacks;
    if (opts.fadeTriggerSec != null) this.fadeTriggerSec = opts.fadeTriggerSec;
    if (opts.fadeDurationMs != null) this.fadeDurationMs = opts.fadeDurationMs;
    if (this._ready) return;
    // Coalesce concurrent init calls (StrictMode double-invoke).
    if (this._initPromise) return this._initPromise;
    this._initPromise = (async () => {
      const YT = await loadYouTubeAPI();
      createHostDiv('yt-player-a');
      createHostDiv('yt-player-b');
      // onPlayerError bridge — YT.Player fires onError when a video is
      // un-embeddable (101/150), removed (100), or invalid (2/5). Without
      // this hook the player sat at state=-1 forever and the queue never
      // advanced. Skip forward on any embed/playback rejection.
      //
      // Iter 85 refinement — filter to HARD embed/removal errors only.
      // Codes 2 (invalid param) and 5 (HTML5) can fire transiently during
      // the mute()+loadVideoById() sequence in play() on perfectly good
      // tracks; auto-skipping on those caused a 0→2 cascade on any
      // embeddable playlist. We now only skip on 100/101/150 which are
      // the truly-terminal "this specific video will never play here"
      // codes. Also debounce: don't skip the same currentIndex twice
      // within 1.5s (protects against rapid duplicate errors from both
      // slot A + slot B firing simultaneously during a crossfade).
      const HARD_EMBED_ERROR_CODES = new Set([100, 101, 150]);
      // Cascade detection — if we skip through 4+ tracks in <3s, the
      // whole playlist / domain is un-embeddable. Fire onEnded so the
      // UI can surface a "no playable tracks" state instead of leaving
      // the player wedged at UNSTARTED forever. Iter 86 exposed this.
      let cascadeCount = 0;
      let cascadeStartedAt = 0;
      let lastSkippedIndex = -1;
      let lastSkippedAt = 0;
      const onPlayerError = ({ code, host_id }) => {
        if (!HARD_EMBED_ERROR_CODES.has(code)) return;
        // Which slot threw? The idle player holds the PREWARMED next track
        // (cued ahead for the crossfade). An error from the IDLE player means
        // the *upcoming* track is unplayable — it must NEVER skip the track
        // currently playing. Doing so cut the current song short AND then hit
        // the bad track's own error → a jump of two tracks ("2 songs skipped").
        // Record the bad id so prewarm/crossfade route around it; the track is
        // skipped cleanly if/when it would actually become active.
        const erroredSlot = host_id === 'yt-player-b' ? 'B' : 'A';
        if (erroredSlot !== this.active) {
          if (this._prewarmedFor) this._unplayableIds.add(this._prewarmedFor);
          this._prewarmedFor = null;
          return;
        }
        const now = Date.now();
        if (this.currentIndex === lastSkippedIndex && now - lastSkippedAt < 1500) return;
        lastSkippedIndex = this.currentIndex;
        lastSkippedAt = now;
        const curId = this.tracks[this.currentIndex]?.id;
        if (curId) this._unplayableIds.add(curId);
        // Track cascade — reset if last skip was >3s ago.
        if (now - cascadeStartedAt > 3000) {
          cascadeCount = 1;
          cascadeStartedAt = now;
        } else {
          cascadeCount += 1;
        }
        const badTrack = this.tracks[this.currentIndex];
        try { this._callbacks.onPlayerError?.({ code, track: badTrack, cascadeCount }); } catch { /* noop */ }
        if (cascadeCount >= 4) {
          // Whole playlist / domain looks un-embeddable — try the next
          // scheduled playlist instead of going silent.
          this._isPlaying = false;
          try { this._callbacks.onQueueExhausted?.(); } catch { /* noop */ }
          return;
        }
        if (this.currentIndex < this.tracks.length - 1) {
          this.next();
        } else {
          // End of queue AND still erroring — advance to the next playlist.
          this._isPlaying = false;
          try { this._callbacks.onQueueExhausted?.(); } catch { /* noop */ }
        }
      };
      this.players.A = await createPlayer(YT, 'yt-player-a', { onPlayerError });
      this.players.B = await createPlayer(YT, 'yt-player-b', { onPlayerError });
      try { this.players.A.setVolume(100); this.players.B.setVolume(0); } catch { /* noop */ }
      this._ready = true;
      this._startTick();
      ['A', 'B'].forEach((slot) => {
        const p = this.players[slot];
        try {
          p.addEventListener('onStateChange', (ev) => this._onPlayerStateChange(slot, ev.data));
        } catch { /* noop */ }
      });
    })();
    return this._initPromise;
  }

  _startTick() {
    if (this._tick) this._tick.clear();
    // Web-Worker-backed interval: main-thread setInterval throttles to ~1/min
    // on hidden/backgrounded tabs, which killed progress polling, pause
    // detection and crossfade arming exactly when the operator's tablet was
    // screen-locked. The worker timer is not throttled. (June 2026)
    this._tick = createBackgroundInterval(() => this._onTick(), 500);
  }

  _activePlayer() { return this.players[this.active]; }
  _idlePlayer() { return this.players[this.active === 'A' ? 'B' : 'A']; }

  _onTick() {
    if (!this._ready) return;
    const p = this._activePlayer();
    if (!p) return;
    let progressMs = 0, durationMs = 0, ytState = -1;
    try {
      progressMs = Math.floor((p.getCurrentTime() || 0) * 1000);
      durationMs = Math.floor((p.getDuration() || 0) * 1000);
      ytState = p.getPlayerState?.() ?? -1;
    } catch { /* noop */ }
    // Poll state in addition to onStateChange listener — some browsers
    // don't reliably fire the listener across every state transition.
    // 1 = PLAYING, 2 = PAUSED, 3 = BUFFERING, 5 = CUED, 0 = ENDED, -1 unstarted
    if (ytState === 1) {
      this._isPlaying = true;
      // Deferred unmute after the autoplay-policy muted-load workaround.
      if (this._pendingUnmute) {
        try { p.unMute(); p.setVolume(this._masterVolume); } catch { /* noop */ }
        this._pendingUnmute = false;
      }
    } else if (ytState === 2 || ytState === 5 || ytState === -1) {
      this._isPlaying = false;
      // Involuntary background pause recovery (June 2026): when the browser
      // or OS pauses our audio while backgrounded/screen-locked — but the
      // operator never hit pause (`_playIntent` still true) — immediately
      // re-issue playVideo(). A deliberate pause() clears `_playIntent`, so
      // this never fights the operator. Skipped during a crossfade, an ad
      // hard-cut, and the track-change grace window (the muted-load autoplay
      // dance transiently reports PAUSED/CUED on a perfectly good track).
      if (
        ytState === 2 &&
        this._playIntent &&
        !this._fade &&
        !this._adPlaying &&
        Date.now() >= this._adGraceUntil
      ) {
        try { p.playVideo(); } catch { /* noop */ }
      }
    }

    const currentTrack = this.tracks[this.currentIndex] || null;
    const _nextIdx = this._nextTrackIdx(this.currentIndex);
    const nextTrack = _nextIdx >= 0 ? (this.tracks[_nextIdx] || null) : null;

    // Fire state callback each tick so UI stays in sync
    try {
      this._callbacks.onState?.({
        isPlaying: this._isPlaying,
        progressMs,
        durationMs,
        activeTrack: currentTrack,
        nextTrack,
        activeSlot: this.active,
      });
    } catch { /* noop */ }

    // ---------- Ad detection ----------
    // Compare currently-loaded videoId to what we queued. If they differ,
    // YouTube is serving an ad in-between. We mute the player to spare
    // the venue from hearing "GEICO GEICO GEICO" and surface a subtle
    // pill via onAdDetected/onAdEnded. Skipped during: crossfade (both
    // players overlap), grace window just after a track change (the
    // iframe may still report the previous videoId briefly), and while
    // no expected track is loaded.
    this._checkForAd(p, currentTrack, ytState);

    // Prewarm the idle player once the active track starts playing — but
    // never cue a track YouTube already rejected (it would just re-throw its
    // embed error on the idle player every tick).
    if (nextTrack && !this._unplayableIds.has(nextTrack.id) && this._prewarmedFor !== nextTrack.id) {
      try {
        const idle = this._idlePlayer();
        idle.setVolume(0);
        idle.cueVideoById(nextTrack.id);
        this._prewarmedFor = nextTrack.id;
      } catch (e) { /* noop */ }
    }

    // Arm the crossfade at (duration - trigger)
    if (durationMs > 0 && currentTrack && this._crossfadeArmedForId !== currentTrack.id) {
      const triggerAtMs = durationMs - this.fadeTriggerSec * 1000;
      if (progressMs >= triggerAtMs && progressMs < durationMs) {
        this._crossfadeArmedForId = currentTrack.id;
        this._startCrossfade();
      }
    }
  }

  _checkForAd(activePlayer, expectedTrack, ytState) {
    if (!activePlayer || !expectedTrack || !expectedTrack.id) return;
    // Only run while actually playing. State 3 (BUFFERING) reads a stale
    // video_id for ~200ms after load — treat as inconclusive.
    if (ytState !== 1) return;
    if (this._fade) return;                         // crossfade in progress
    if (Date.now() < this._adGraceUntil) return;    // just switched tracks
    let currentVid = null;
    try { currentVid = activePlayer.getVideoData?.()?.video_id || null; } catch { /* noop */ }
    if (!currentVid) return;                        // player still initialising

    // Some tracks legitimately play back under a DIFFERENT canonical
    // video_id than the one we requested — YouTube Music "Art Tracks"
    // (the "- Topic" audio uploads our playlist importer resolves), plus a
    // few region/age variants. Comparing to the requested id therefore
    // produced a PERMANENT false "ad" on those tracks, and the old code
    // then hard-skipped to the next track every ~1s, cascading through the
    // whole playlist — the "songs never play full / jumps between songs /
    // stuck in a loop" bug. Fix (June 2026): (1) never hard-skip on a
    // mismatch — mute-and-wait is non-destructive; (2) once a stable
    // alternate id has been accepted for the current track, stop flagging
    // it at all.
    const isExpected = currentVid === expectedTrack.id || currentVid === this._acceptedAltId;
    const isAd = !isExpected;

    if (isAd) {
      this._adMismatchStreak = (this._adMismatchStreak || 0) + 1;
    } else {
      this._adMismatchStreak = 0;
    }
    // Require a sustained mismatch (~3s) before muting. A genuine
    // pre-roll/mid-roll ad easily clears this; transient reads during load,
    // seek and the muted-load autoplay workaround do not. Embedded IFrame
    // players never inherit a user's YouTube Premium, so real ads can still
    // appear regardless of the operator's subscription — this only removes
    // the destructive false-positive skipping.
    const AD_CONFIRM_TICKS = 6;
    const AD_MAX_MUTE_MS = 20000;
    const confirmedAd = this._adMismatchStreak >= AD_CONFIRM_TICKS;

    if (confirmedAd && !this._adPlaying) {
      // Spare the venue the ad audio by muting. We deliberately DO NOT
      // hard-skip — skipping on a video_id mismatch churned through good
      // tracks. The real track's audio returns the instant the ad ends.
      this._adPlaying = true;
      this._adMutedAt = Date.now();
      this._adVolumeBeforeMute = this._masterVolume;
      try { activePlayer.setVolume(0); } catch { /* noop */ }
      // eslint-disable-next-line no-console
      console.info('[ytdriver] ad suspected — muting (no skip)', {
        expected: expectedTrack.id, seen: currentVid,
      });
      try {
        this._callbacks.onAdDetected?.({ expected: expectedTrack.id, seen: currentVid });
      } catch { /* noop */ }
    } else if (this._adPlaying && isAd) {
      // Mismatch has persisted well past any real ad length. This is not an
      // ad — it's a stable canonical id for THIS track. Accept it and
      // unmute so we never mute this track again.
      if (Date.now() - (this._adMutedAt || 0) > AD_MAX_MUTE_MS) {
        this._acceptedAltId = currentVid;
        this._adPlaying = false;
        try { activePlayer.setVolume(this._adVolumeBeforeMute); } catch { /* noop */ }
        // eslint-disable-next-line no-console
        console.info('[ytdriver] mismatch is stable — accepting canonical id', currentVid);
        try { this._callbacks.onAdEnded?.(); } catch { /* noop */ }
      }
    } else if (!isAd && this._adPlaying) {
      // Ad ended, the queued track is back on the player. Restore volume.
      this._adPlaying = false;
      try { activePlayer.setVolume(this._adVolumeBeforeMute); } catch { /* noop */ }
      // eslint-disable-next-line no-console
      console.info('[ytdriver] ad ended — restoring volume');
      try { this._callbacks.onAdEnded?.(); } catch { /* noop */ }
    }
  }

  _armAdGrace(ms = 3000) {
    // Suppress ad detection for `ms` after any track change. The iframe
    // often reports the previous videoId for ~1-2s after loadVideoById.
    this._adGraceUntil = Date.now() + ms;
    this._adMismatchStreak = 0;   // reset the confirmation streak on any track change
    this._acceptedAltId = null;   // the accepted canonical id is per-track
    this._adMutedAt = 0;
    // If we were previously in an ad state (edge case), clear it so a
    // legitimate track-change doesn't leave the pill stuck on-screen.
    if (this._adPlaying) {
      this._adPlaying = false;
      try { this._activePlayer()?.setVolume(this._adVolumeBeforeMute); } catch { /* noop */ }
      try { this._callbacks.onAdEnded?.(); } catch { /* noop */ }
    }
  }

  _startCrossfade() {
    if (this._fade) return;
    const active = this._activePlayer();
    const idle = this._idlePlayer();
    if (!active || !idle) return;
    const activeSlot = this.active;
    const nextIdx = this._nextTrackIdx(this.currentIndex);
    const nextTrack = nextIdx >= 0 ? this.tracks[nextIdx] : null;
    if (!nextTrack || this._unplayableIds.has(nextTrack.id)) return;

    // Start idle playing at volume 0
    try { idle.setVolume(0); idle.playVideo(); } catch { /* noop */ }

    const stepMs = 100;
    const steps = Math.max(1, Math.floor(this.fadeDurationMs / stepMs));
    let i = 0;
    this._fade = createBackgroundInterval(() => {
      i += 1;
      const t = Math.min(1, i / steps);
      const activeVol = Math.max(0, Math.round(this._masterVolume * (1 - t)));
      const idleVol = Math.min(this._masterVolume, Math.round(this._masterVolume * t));
      try { active.setVolume(activeVol); idle.setVolume(idleVol); } catch { /* noop */ }
      if (i >= steps) {
        this._fade.clear();
        this._fade = null;
        // Swap roles
        try { active.stopVideo(); } catch { /* noop */ }
        this.active = activeSlot === 'A' ? 'B' : 'A';
        this.currentIndex = nextIdx;
        this._prewarmedFor = null;
        this._crossfadeArmedForId = null;
        this._armAdGrace();
        try {
          this._callbacks.onTrackChanged?.(
            this.tracks[this.currentIndex] || null,
            this.tracks[this._nextTrackIdx(this.currentIndex)] || null,
          );
          this._callbacks.onTrackDone?.(this.currentIndex - 1);
        } catch { /* noop */ }
      }
    }, stepMs);
  }

  _onPlayerStateChange(slot, state) {
    // 0 = ENDED, 1 = PLAYING, 2 = PAUSED, 3 = BUFFERING, 5 = CUED
    if (slot !== this.active) return;
    if (state === 1) this._isPlaying = true;
    if (state === 2) {
      this._isPlaying = false;
      // Event-driven involuntary-pause recovery (resilience hardening): the
      // iframe fires this state change even when the tab is backgrounded and
      // the worker tick is being throttled, so recovery is immediate rather
      // than waiting out a (possibly slowed) tick. Same guards as the tick:
      // never fight a deliberate pause (`_playIntent` cleared), a crossfade,
      // an ad hard-cut, or the muted-load autoplay grace window.
      if (
        this._playIntent &&
        !this._fade &&
        !this._adPlaying &&
        Date.now() >= this._adGraceUntil
      ) {
        try { this._activePlayer()?.playVideo(); } catch { /* noop */ }
      }
    }
    if (state === 0) {
      // Natural end without crossfade catching it (very short track etc.)
      this._isPlaying = false;
      if (this._fade) return;   // crossfade in progress; ignore
      const target = this._nextTrackIdx(this.currentIndex);
      if (target >= 0) {
        // Force-advance to the next PLAYABLE track (wraps at end so a long
        // shift never goes silent; skips known-bad ids).
        this.currentIndex = target;
        this._prewarmedFor = null;
        this._crossfadeArmedForId = null;
        const next = this.tracks[this.currentIndex];
        try {
          const p = this._activePlayer();
          p.setVolume(this._masterVolume);
          p.loadVideoById(next.id);
        } catch { /* noop */ }
        try {
          this._callbacks.onTrackChanged?.(next, this.tracks[this._nextTrackIdx(this.currentIndex)] || null);
          this._callbacks.onTrackDone?.(this.currentIndex - 1);
        } catch { /* noop */ }
      } else {
        try { this._callbacks.onQueueExhausted?.(); } catch { /* noop */ }
      }
    }
  }

  // -------- Public control API --------

  loadQueue(tracks, startIndex = 0) {
    const normalised = (Array.isArray(tracks) ? tracks : [])
      .map(normalizeTrack)
      .filter(Boolean);
    this.tracks = normalised;
    this.currentIndex = Math.max(0, Math.min(startIndex, this.tracks.length - 1));
    this._prewarmedFor = null;
    this._crossfadeArmedForId = null;
    this._armAdGrace();
    if (!this.tracks.length) {
      // eslint-disable-next-line no-console
      console.warn('[YT driver] loadQueue got 0 usable tracks (missing videoId on all inputs)');
      return;
    }
    const active = this._activePlayer();
    const idle = this._idlePlayer();
    try {
      active.setVolume(this._masterVolume);
      // cue (not load) so we don't hit autoplay-policy silent-block on
      // hydration when there's no user gesture yet. User's Play tap will
      // switch cued→playing via `loadVideoById` in play().
      active.cueVideoById(this.tracks[this.currentIndex].id);
      if (this.tracks[this.currentIndex + 1]) {
        idle.setVolume(0);
        idle.cueVideoById(this.tracks[this.currentIndex + 1].id);
        this._prewarmedFor = this.tracks[this.currentIndex + 1].id;
      }
    } catch { /* noop */ }
  }

  // Next index > `from` whose track YouTube hasn't rejected; may return
  // `tracks.length` (meaning: nothing playable left → end of queue).
  _nextPlayableIndex(from) {
    let i = from + 1;
    while (i < this.tracks.length && this._unplayableIds.has(this.tracks[i]?.id)) i += 1;
    return i;
  }

  // The next PLAYABLE index after `from` within THIS playlist, or -1 at the
  // end (no wrap). The booth advances to the next scheduled playlist on -1
  // (see MusicContext.onQueueExhausted) rather than looping. -1 also covers
  // the "every remaining track is unplayable" dead end — advancing to a fresh
  // playlist is a better recovery than replaying known-bad tracks.
  _nextTrackIdx(from) {
    for (let i = from + 1; i < this.tracks.length; i += 1) {
      if (!this._unplayableIds.has(this.tracks[i]?.id)) return i;
    }
    return -1;
  }

  next() {
    this._playIntent = true;
    if (this._fade) { this._fade.clear(); this._fade = null; }
    try { this._activePlayer()?.stopVideo(); } catch { /* noop */ }
    const target = this._nextTrackIdx(this.currentIndex);
    if (target < 0) {
      try { this._callbacks.onQueueExhausted?.(); } catch { /* noop */ }
      return;
    }
    this.currentIndex = target;
    this._prewarmedFor = null;
    this._crossfadeArmedForId = null;
    this._armAdGrace();
    const track = this.tracks[this.currentIndex];
    try {
      const p = this._activePlayer();
      p.setVolume(this._masterVolume);
      p.loadVideoById(track.id);
    } catch { /* noop */ }
    try { this._callbacks.onTrackChanged?.(track, this.tracks[this._nextTrackIdx(this.currentIndex)] || null); } catch { /* noop */ }
  }

  play() {
    this._playIntent = true;
    const active = this._activePlayer();
    if (!active) return;
    try {
      const cur = this.tracks[this.currentIndex];
      // If a video is already cued (state=5) with the right id, playVideo()
      // is the most reliable user-gesture path. Otherwise cue then play.
      const currentVid = active.getVideoData?.()?.video_id || null;
      if (cur && cur.id && currentVid !== cur.id) {
        // Autoplay-policy workaround: mute + load; tick will unmute on PLAYING.
        try { active.mute(); } catch { /* noop */ }
        active.loadVideoById(cur.id);
        this._pendingUnmute = true;
        // Feb 2026 fix: don't rely purely on onStateChange(PLAYING) to
        // start playback — under Chromium autoplay policy for hidden
        // iframes that event sometimes never fires. Explicitly call
        // playVideo() a tick later so we don't sit forever in CUED (5).
        // The mute() call above keeps us inside the autoplay allowlist,
        // and playVideo() on a just-cued player is idempotent.
        setTimeout(() => {
          try {
            const st = active.getPlayerState?.();
            // Only nudge if we're not already PLAYING(1) / BUFFERING(3).
            if (st !== 1 && st !== 3) active.playVideo();
          } catch { /* noop */ }
        }, 250);
        // Second-chance nudge for slow networks / cold YT boots.
        setTimeout(() => {
          try {
            const st = active.getPlayerState?.();
            if (st !== 1 && st !== 3) active.playVideo();
          } catch { /* noop */ }
        }, 1500);
        return;
      }
      active.playVideo();
    } catch { /* noop */ }
  }

  pause() {
    this._playIntent = false;
    try { this._activePlayer()?.pauseVideo(); } catch { /* noop */ }
  }

  previous() {
    this._playIntent = true;
    if (this._fade) { this._fade.clear(); this._fade = null; }
    if (this.currentIndex <= 0) {
      try { this._activePlayer()?.seekTo(0, true); this._activePlayer()?.playVideo(); } catch { /* noop */ }
      return;
    }
    this.currentIndex -= 1;
    this._prewarmedFor = null;
    this._crossfadeArmedForId = null;
    this._armAdGrace();
    const track = this.tracks[this.currentIndex];
    try {
      const p = this._activePlayer();
      p.setVolume(this._masterVolume);
      p.loadVideoById(track.id);
    } catch { /* noop */ }
    try { this._callbacks.onTrackChanged?.(track, this.tracks[this.currentIndex + 1] || null); } catch { /* noop */ }
  }

  setVolume(volume0to100) {
    this._masterVolume = Math.max(0, Math.min(100, Math.round(volume0to100)));
    try { this._activePlayer()?.setVolume(this._masterVolume); } catch { /* noop */ }
  }

  destroy() {
    if (this._tick) this._tick.clear();
    if (this._fade) this._fade.clear();
    try { this.players.A?.destroy(); } catch { /* noop */ }
    try { this.players.B?.destroy(); } catch { /* noop */ }
    this._ready = false;
  }
}

// Singleton
let _instance = null;
export function getYouTubeDriver() {
  if (!_instance) {
    _instance = new YouTubeDualDriver();
    // Expose for debugging (browser console) — no perf impact.
    if (typeof window !== 'undefined') window.__ytdriver = _instance;
  }
  return _instance;
}
